/**
 * Wrappers for the ingest callables in suite-io/functions/src/handlers/ingest/.
 *
 * All callables live in nyc-suite — same Firebase project as suite-io, so no
 * cross-project auth bridge. Errors propagate as Firebase HttpsError; callers
 * can read `err.code` (e.g. "permission-denied", "failed-precondition").
 */

import { callable } from "../firebase.js";

const call = async (name, payload) => {
  const fn = callable(name);
  const result = await fn(payload);
  return result.data;
};

export const ingest = {
  /**
   * Arm a subject. Send EXACTLY ONE of:
   *   - qrToken      (from a QR scan; backend hashes + looks up by qrTokenHash)
   *   - rosterEntryId (from manual lookup; raw token never leaves the server)
   * Optionally pass sessionId for multi-day jobs.
   */
  arm: ({ jobId, sessionId, qrToken, rosterEntryId, deviceId, clientLeaseId }) =>
    call("armParticipant", {
      jobId,
      sessionId: sessionId ?? null,
      deviceId,
      clientLeaseId,
      // Firebase callable client serializes undefined → null on the wire, and
      // the backend schema's .optional() accepts undefined but rejects null.
      // Only include each field when actually provided.
      ...(qrToken !== undefined ? { qrToken } : {}),
      ...(rosterEntryId !== undefined ? { rosterEntryId } : {}),
    }),

  /** Cancel the currently armed/counting-down subject. Device-scoped. */
  cancel: ({ jobId, leaseId, deviceId }) =>
    call("cancelArm", { jobId, leaseId, deviceId }),

  /**
   * Begin the countdown (or fire immediately if countdownSeconds=0).
   *
   * Return shape varies by mode:
   *   classic (countdownSeconds > 0): { ok, state }
   *   instant (countdownSeconds = 0): { ok, state, captureAttemptId, sn }
   * In instant mode the SN is minted server-side in this same call; the
   * client never follows up with confirmCapture.
   */
  start: ({ jobId, leaseId, deviceId }) =>
    call("startCapture", { jobId, leaseId, deviceId }),

  /** Rig calls this when its local countdown completes. Creates captureAttempt. */
  confirm: ({ jobId, leaseId, deviceId }) =>
    call("confirmCapture", { jobId, leaseId, deviceId }),

  /** Watcher / xangle-bridge posts manifest + files when capture is done. */
  recordComplete: ({
    jobId,
    captureAttemptId,
    files,
    manifestPath,
    xangleSessionId,
    thumbnailUrl,
    captureFolderPath,
    fileCount,
    primaryAssetPath,
  }) =>
    call("recordCaptureComplete", {
      jobId,
      captureAttemptId,
      files,
      manifestPath,
      // Same undefined→null wire conversion guard as arm() above.
      ...(xangleSessionId !== undefined ? { xangleSessionId } : {}),
      ...(thumbnailUrl !== undefined ? { thumbnailUrl } : {}),
      ...(captureFolderPath !== undefined ? { captureFolderPath } : {}),
      ...(fileCount !== undefined ? { fileCount } : {}),
      ...(primaryAssetPath !== undefined ? { primaryAssetPath } : {}),
    }),

  /** Operator-driven recovery for a stuck capture attempt. */
  reconcile: ({ jobId, captureAttemptId, decision, note, files, manifestPath }) =>
    call("reconcileCapture", {
      jobId,
      captureAttemptId,
      decision,
      // Same undefined→null wire conversion guard as arm() above.
      ...(note !== undefined ? { note } : {}),
      ...(files !== undefined ? { files } : {}),
      ...(manifestPath !== undefined ? { manifestPath } : {}),
    }),

  /**
   * Bulk-create roster entries from a parsed producer CSV/XLSX.
   * Caller parses the file client-side and sends structured rows.
   * Rows missing first or last name are skipped server-side.
   * Returns { ok, importBatchId, imported[], skipped[] }.
   */
  importRoster: ({ jobId, rows }) => call("importRoster", { jobId, rows }),
};

// Stable per-tab device id. Survives reloads, resets on private/incognito.
export const getDeviceId = () => {
  const key = "ingest.deviceId";
  let id = localStorage.getItem(key);
  if (!id) {
    id = `dev_${crypto.randomUUID()}`;
    localStorage.setItem(key, id);
  }
  return id;
};

// Client-side lease id generator — passed to arm() and matched on cancel/start.
export const newLeaseId = () => `lease_${crypto.randomUUID()}`;
